import { useState } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import BuscadorCliente from './BuscadorCliente'
import { recibosPreseaApi } from '../api/pagos'
import { fmtFecha, fmtMonto } from '../utils/fecha'

/*
 * Recibo hecho EN PRESEA → importado a LGR (Martin, 26 ago 2026).
 *
 * Mientras las retenciones del recibo no salgan por el WS, el recibo con
 * retenciones se hace en Presea. Acá el operador carga el número, LGR lo
 * lee por DETALLE_COMPRO y muestra lo que Presea tiene (formulario, fecha,
 * importe, retenciones, cliente) y lo importa como cobro ACREDITADO.
 *
 * Dos usos (Martin, 7 sep 2026): con `nv` el cobro queda imputado a esa NV
 * (detalle de NV — borrador o procesada); sin `nv` queda a cuenta (recibo
 * libre desde Recibos/Cobranzas) y se imputa después desde el detalle del
 * cobro. `cliente` es opcional: si no viene, se elige acá adentro.
 */
const RET = { iibb: 'Ret. IIBB', iva: 'Ret. IVA', ganancias: 'Ret. Ganancias', suss: 'Ret. SUSS', sellos: 'Ret. Sellos' }

export default function ModalReciboPresea({ nv, cliente: clienteInicial, onClose, onImportado }) {
  // Contra quién se verifica/importa: el cliente de la NV o el elegido acá.
  const [cliente, setCliente] = useState(clienteInicial || null)
  const ref = nv ? { nv_id: nv.id } : { cliente_id: cliente?.id }
  const clienteDestino = nv ? nv.cliente : cliente
  const [numero, setNumero] = useState('')
  const [verificando, setVerificando] = useState(false)
  const [resultado, setResultado] = useState(null)      // { recibos, otros_formularios }
  const [elegido, setElegido] = useState(null)          // el recibo (formulario) elegido
  const [importando, setImportando] = useState(false)
  const [error, setError] = useState('')

  const verificar = async (e) => {
    e?.preventDefault()
    if (!numero.trim()) return
    if (!nv && !cliente) { setError('Elegí primero el cliente del recibo.'); return }
    setVerificando(true); setError(''); setResultado(null); setElegido(null)
    try {
      const { data } = await recibosPreseaApi.verificar(ref, numero.trim())
      setResultado(data)
      if (data.recibos.length === 1) setElegido(data.recibos[0])
      if (data.recibos.length === 0) {
        setError(data.otros_formularios.length
          ? `Presea no tiene un RECIBO con ese número. Con ese número hay: ${data.otros_formularios.join(', ')}.`
          : 'Presea no tiene ningún comprobante con ese número.')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo consultar Presea.')
    } finally { setVerificando(false) }
  }

  const importar = async () => {
    if (!elegido) return
    setImportando(true); setError('')
    try {
      const { data } = await recibosPreseaApi.importar(ref, elegido.numero, elegido.formulario)
      onImportado(data)
    } catch (err) {
      const errs = err.response?.data?.errors
      setError(errs ? Object.values(errs).flat().join(' · ') : (err.response?.data?.message || 'No se pudo importar.'))
    } finally { setImportando(false) }
  }

  const puedeImportar = elegido && elegido.cliente_coincide && !elegido.ya_importado_pago_id

  return (
    <Modal title={`Recibo hecho en Presea — ${nv ? nv.numero_lgr : (clienteDestino?.nombre || 'cobranza libre')}`} onClose={onClose} maxWidth="560px">
      <p style={st.ayuda}>
        El recibo ya se hizo en Presea (por ejemplo, con retenciones). Cargá su número: LGR lo lee de
        Presea y lo trae tal cual está allá, como cobro acreditado{nv
          ? ' imputado a esta NV.'
          : ' a cuenta del cliente — después lo imputás a sus comprobantes desde el detalle del cobro.'}
      </p>

      {!nv && (
        <div style={{ marginTop: '0.75rem' }}>
          <label style={st.label}>Cliente del recibo</label>
          <BuscadorCliente key={cliente?.id || 'no'} value={cliente}
            onChange={(c) => { setCliente(c); setResultado(null); setElegido(null); setError('') }} />
        </div>
      )}

      <form onSubmit={verificar} style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <input
          style={{ ...st.input, flex: 1 }}
          value={numero}
          onChange={(e) => { setNumero(e.target.value); setResultado(null); setElegido(null); setError('') }}
          placeholder="Número de recibo en Presea (ej. 200051298)"
          autoFocus
          inputMode="numeric"
        />
        <Boton variante="secondary" type="submit" disabled={verificando || !numero.trim() || (!nv && !cliente)}>
          {verificando ? 'Consultando…' : 'Verificar'}
        </Boton>
      </form>

      {error && <div style={st.errorBox}>{error}</div>}

      {resultado && resultado.recibos.length > 1 && (
        <div style={{ marginTop: '0.75rem' }}>
          <label style={st.label}>Hay más de un recibo con ese número — ¿cuál es?</label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {resultado.recibos.map((r) => (
              <Boton key={r.formulario} variante={elegido?.formulario === r.formulario ? 'primary' : 'secondary'} sm type="button"
                onClick={() => setElegido(r)}>
                {r.formulario} · {fmtMonto(r.importe)}
              </Boton>
            ))}
          </div>
        </div>
      )}

      {elegido && (
        <div style={st.ficha}>
          <div style={st.fila}><span>Comprobante</span><b>{elegido.formulario} {elegido.numero}</b></div>
          <div style={st.fila}><span>Fecha</span><b>{elegido.fecha ? fmtFecha(elegido.fecha) : '—'}</b></div>
          <div style={st.fila}><span>Importe</span><b>{fmtMonto(elegido.importe)}</b></div>
          {Object.entries(elegido.retenciones || {}).map(([k, v]) => (
            <div key={k} style={st.fila}><span>{RET[k] || k}</span><b>{fmtMonto(v)}</b></div>
          ))}
          {elegido.total_retenciones > 0 && (
            <div style={st.fila}><span>Total retenciones</span><b>{fmtMonto(elegido.total_retenciones)}</b></div>
          )}
          <div style={st.fila}>
            <span>Cliente en Presea</span>
            <b style={{ color: elegido.cliente_coincide ? '#166534' : '#b91c1c' }}>
              {elegido.cliente_codigo || '—'} {elegido.cliente_coincide
                ? (nv ? '✓ es el de la NV' : '✓ es el cliente elegido')
                : (nv ? '✕ NO es el de la NV' : '✕ NO es el cliente elegido')}
            </b>
          </div>
          {elegido.ya_importado_pago_id && (
            <div style={st.aviso}>Este recibo ya está en LGR (cobro #{elegido.ya_importado_pago_id}).</div>
          )}
          {!elegido.cliente_coincide && (
            <div style={st.aviso}>El recibo es de otro cliente ({elegido.cliente_codigo}); {nv ? 'la NV es del' : 'estás cobrando al'} {clienteDestino?.codigo_presea || 'cliente sin número'}. No se importa.</div>
          )}

        </div>
      )}

      <div style={st.actions}>
        <Boton variante="ghost" type="button" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" type="button" disabled={!puedeImportar || importando} onClick={importar}>
          {importando ? 'Importando…' : (nv ? 'Importar recibo a la NV' : 'Importar recibo')}
        </Boton>
      </div>
    </Modal>
  )
}

const st = {
  ayuda: { margin: 0, fontSize: '0.85rem', color: '#475569', lineHeight: 1.5 },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.875rem', boxSizing: 'border-box' },
  label: { display: 'block', fontWeight: 600, fontSize: '0.8125rem', color: '#374151', marginBottom: '0.25rem' },
  errorBox: { marginTop: '0.75rem', background: '#fee2e2', color: '#991b1b', padding: '0.5rem 0.75rem', borderRadius: 6, fontSize: '0.85rem' },
  ficha: { marginTop: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.75rem 0.9rem', background: '#f8fafc', fontSize: '0.85rem' },
  fila: { display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '3px 0', color: '#475569' },
  aviso: { marginTop: '0.5rem', background: '#fef3c7', color: '#92400e', padding: '0.4rem 0.6rem', borderRadius: 6, fontSize: '0.8rem' },
  nota: { display: 'block', marginTop: 3, fontSize: '0.75rem', color: '#94a3b8' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' },
}
